import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getQcMinDurationConfigApi,
  updateQcMinDurationConfigApi,
} from '@/lib/reports-api';
import {
  canSaveQcMinDuration,
  DEFAULT_QC_MIN_DURATION_RANGE,
  isQcMinDurationInRange,
  QC_MIN_DURATION_LABEL,
  qcMinDurationRangeHint,
  type QcMinDurationRange,
} from '@/lib/qc-min-duration';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #232 — 管理员「系统设置 → KPI考核 → 最短有效质检时长」。
 *
 * 真实 GET/PATCH /system/qc-min-duration-config（GET 任意已登录可读，PATCH 仅 ADMIN+，
 * 按 orgId 隔离）。该阈值（默认 60 秒，区间 10-600）即移动端质检录音停止后判定
 * 「无效质检并作废」的最短时长：实时转写为空 / 过短 / 断流 / 失败且本次时长 < 阈值时，
 * 直接作废不生成 AI 评分报告。越界由服务端钳制；保存后运行时即时生效，无需重启。
 *
 * 渲染外的「能否保存 / 区间校验 / 文案」逻辑下沉到纯模块 @/lib/qc-min-duration（admin
 * vitest 无渲染夹具），本组件仅薄包装；绝不前端 state 模拟，数据全部来自真实接口。
 */
const INPUT =
  'w-28 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

export function QcMinDurationSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'qc-min-duration-config'],
    queryFn: getQcMinDurationConfigApi,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setDraft(String(query.data.minValidDurationSec));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (seconds: number) => updateQcMinDurationConfigApi(seconds),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(String(data.minValidDurationSec));
      await queryClient.invalidateQueries({ queryKey: ['system', 'qc-min-duration-config'] });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title={QC_MIN_DURATION_LABEL}>
        <LoadingState label="正在加载最短有效质检时长…" />
      </Card>
    );
  }
  if (query.isError || draft === null || !query.data) {
    return (
      <Card title={QC_MIN_DURATION_LABEL}>
        <ErrorState
          title="加载最短有效质检时长失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const range: QcMinDurationRange = {
    minDurationSec: query.data.minDurationSec ?? DEFAULT_QC_MIN_DURATION_RANGE.minDurationSec,
    maxDurationSec: query.data.maxDurationSec ?? DEFAULT_QC_MIN_DURATION_RANGE.maxDurationSec,
    defaultDurationSec:
      query.data.defaultDurationSec ?? DEFAULT_QC_MIN_DURATION_RANGE.defaultDurationSec,
  };
  const localInvalid = !isQcMinDurationInRange(draft, range);
  const canSave = canSaveQcMinDuration({
    canEdit,
    pending: mutation.isPending,
    raw: draft,
    range,
  });

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    // 只读 / 越界 / 保存中一律不触发真实 PATCH（与 canSaveQcMinDuration 同源）。
    if (!canSave) return;
    mutation.mutate(Number(draft.trim()));
  };

  return (
    <Card
      title={QC_MIN_DURATION_LABEL}
      description="质检录音停止后，若实时转写为空 / 过短 / 断流 / 失败且本次质检时长低于该阈值，移动端判定为「无效质检并作废」，不生成 AI 评分报告（时长达标则回退既有上传 + 云端转写链路）。默认 60 秒，区间 10-600，保存后运行时即时生效。"
    >
      <dl className="mb-4 grid grid-cols-2 gap-y-2 text-sm text-slate-700 sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">当前阈值</dt>
          <dd className="mt-1 font-medium text-slate-900" data-testid="qc-min-duration-current">
            {query.data.minValidDurationSec} 秒
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">允许范围</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {range.minDurationSec}-{range.maxDurationSec} 秒
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">出厂默认</dt>
          <dd className="mt-1 font-medium text-slate-900">{range.defaultDurationSec} 秒</dd>
        </div>
      </dl>

      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-slate-600">
          {QC_MIN_DURATION_LABEL}
          <span className="ml-1 text-xs text-slate-400">（{qcMinDurationRangeHint(range)}）</span>
        </span>
        <input
          className={INPUT}
          type="number"
          inputMode="numeric"
          min={range.minDurationSec}
          max={range.maxDurationSec}
          step={1}
          value={draft}
          disabled={!canEdit}
          onChange={(e) => {
            setSaved(false);
            setDraft(e.target.value);
          }}
          data-testid="qc-min-duration-input"
        />
      </label>

      {localInvalid ? (
        <p className="mt-3 text-xs text-amber-600">
          请输入 {range.minDurationSec}-{range.maxDurationSec} 之间的整数秒。
        </p>
      ) : null}

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            className={PRIMARY_BTN}
            onClick={onSave}
            disabled={!canSave}
            data-testid="qc-min-duration-save"
          >
            {mutation.isPending ? '保存中…' : '保存配置'}
          </button>
          {saved ? (
            <span className="text-xs text-emerald-600">已保存，质检判定即时生效。</span>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改最短有效质检时长。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}
